import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PrivacyControls = ({ user, onDataChange }) => {
  const [privacySettings, setPrivacySettings] = useState({
    profile: {
      showProgressPublicly: user?.privacy?.showProgressPublicly || false,
      showEnergyLevels: user?.privacy?.showEnergyLevels || false,
      showJournalEntries: user?.privacy?.showJournalEntries || false,
      allowPractitionerContact: user?.privacy?.allowPractitionerContact || true
    },
    health: {
      shareHealthDataWithPractitioners: user?.privacy?.shareHealthDataWithPractitioners || true,
      shareDataForResearch: user?.privacy?.shareDataForResearch || false,
      allowHealthDataAnalytics: user?.privacy?.allowHealthDataAnalytics || false,
      anonymousTestimonials: user?.privacy?.anonymousTestimonials || true
    },
    communication: {
      allowCenterContact: user?.privacy?.allowCenterContact || true,
      allowCommunityMessages: user?.privacy?.allowCommunityMessages || false,
      showOnlineStatus: user?.privacy?.showOnlineStatus || false,
      allowLocationTracking: user?.privacy?.allowLocationTracking || false
    },
    data: {
      cookieConsent: user?.privacy?.cookieConsent || true,
      analyticsOptIn: user?.privacy?.analyticsOptIn || false,
      thirdPartyDataSharing: user?.privacy?.thirdPartyDataSharing || false,
      marketingCommunications: user?.privacy?.marketingCommunications || false
    }
  });

  const [showDataExport, setShowDataExport] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);

  const handleSettingChange = (category, setting, value) => {
    setPrivacySettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));
    onDataChange?.();
  };

  const handleDataExport = (format) => {
    // In real app, this would make API call to export user data
    const exportData = {
      profile: user,
      wellnessData: {
        // Would contain actual wellness journey data
        energyLevels: [],
        meditationProgress: [],
        sessionHistory: [],
        journalEntries: []
      },
      bookingHistory: [],
      preferences: privacySettings,
      exportDate: new Date().toISOString(),
      format: format
    };

    const dataStr = format === 'json' ? JSON.stringify(exportData, null, 2) : 'CSV data would be generated here';
    const dataBlob = new Blob([dataStr], { 
      type: format === 'json' ? 'application/json' : 'text/csv' 
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wellness-data-export-${new Date().toISOString().split('T')[0]}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDeleteAccount = () => {
    // In real app, this would show additional confirmation and make API call
    if (window.confirm('Are you absolutely sure you want to delete your account? This action cannot be undone and will permanently delete all your wellness data, booking history, and progress tracking.')) {
      console.log('Initiating account deletion process...');
      // Would redirect to account deletion confirmation page
    }
  };

  const PrivacySection = ({ title, description, icon, settings, category }) => (
    <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
      <div className="px-6 py-4 border-b border-blue-100">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center">
          <Icon name={icon} size={18} className="mr-2 text-blue-600" />
          {title}
        </h3>
        <p className="text-sm text-slate-600 mt-1">{description}</p>
      </div>
      
      <div className="p-6">
        <div className="space-y-4">
          {Object.entries(settings).map(([key, config]) => (
            <div key={key} className="flex items-center justify-between p-4 bg-white rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors duration-200">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-slate-700 mb-1">
                  {config.label}
                  {config.sensitive && (
                    <Icon name="Lock" size={12} className="inline ml-2 text-red-500" />
                  )}
                </h4>
                <p className="text-xs text-slate-600">{config.description}</p>
                {config.warning && (
                  <p className="text-xs text-red-600 mt-1 flex items-center">
                    <Icon name="AlertTriangle" size={12} className="mr-1" />
                    {config.warning}
                  </p>
                )}
              </div>
              
              <button
                onClick={() => handleSettingChange(category, key, !privacySettings[category][key])}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  privacySettings[category][key] ? 'bg-blue-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                    privacySettings[category][key] ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const profileSettings = {
    showProgressPublicly: {
      label: 'Public Wellness Progress',
      description: 'Allow other community members to see your wellness journey progress and achievements',
      sensitive: false
    },
    showEnergyLevels: {
      label: 'Share Energy Level Data',
      description: 'Allow energy level tracking data to be visible to practitioners during sessions',
      sensitive: true
    },
    showJournalEntries: {
      label: 'Public Journal Entries',
      description: 'Make your wellness journal entries visible to the community for inspiration',
      sensitive: true
    },
    allowPractitionerContact: {
      label: 'Practitioner Communications',
      description: 'Allow wellness practitioners and EES center staff to contact you directly',
      sensitive: false
    }
  };

  const healthSettings = {
    shareHealthDataWithPractitioners: {
      label: 'Share Health Information with Practitioners',
      description: 'Allow your health considerations and medical information to be shared with session practitioners',
      sensitive: true,
      warning: 'This helps practitioners provide safer, more personalized sessions'
    },
    shareDataForResearch: {
      label: 'Contribute to Wellness Research',
      description: 'Allow your anonymized wellness data to be used for EES and wellness research studies',
      sensitive: true
    },
    allowHealthDataAnalytics: {
      label: 'Health Data Analytics',
      description: 'Enable advanced analytics on your health data to provide personalized wellness insights',
      sensitive: true
    },
    anonymousTestimonials: {
      label: 'Anonymous Testimonials',
      description: 'Allow your reviews and testimonials to be shared publicly without identifying information',
      sensitive: false
    }
  };

  const communicationSettings = {
    allowCenterContact: {
      label: 'Wellness Center Communications',
      description: 'Allow EES centers and wellness facilities to send you updates and offers',
      sensitive: false
    },
    allowCommunityMessages: {
      label: 'Community Messaging',
      description: 'Allow other community members to send you private messages',
      sensitive: false
    },
    showOnlineStatus: {
      label: 'Online Status Visibility',
      description: 'Show when you\'re online and active in the wellness community',
      sensitive: false
    },
    allowLocationTracking: {
      label: 'Location-Based Services',
      description: 'Allow location tracking to find nearby wellness centers and personalized recommendations',
      sensitive: true,
      warning: 'Used only for finding nearby centers and travel distance calculations'
    }
  };

  const dataSettings = {
    cookieConsent: {
      label: 'Essential Cookies',
      description: 'Allow cookies necessary for basic website functionality and security',
      sensitive: false
    },
    analyticsOptIn: {
      label: 'Usage Analytics',
      description: 'Allow collection of anonymized usage data to improve the platform experience',
      sensitive: false
    },
    thirdPartyDataSharing: {
      label: 'Third-Party Data Sharing',
      description: 'Allow sharing of data with trusted wellness partners and service providers',
      sensitive: true,
      warning: 'Data is only shared with vetted wellness and healthcare partners'
    },
    marketingCommunications: {
      label: 'Marketing Communications',
      description: 'Receive promotional emails and notifications about new wellness services',
      sensitive: false
    }
  };

  return (
    <div className="space-y-8">
      {/* Privacy Overview */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={24} className="text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h2 className="text-xl font-semibold text-blue-800 mb-2">
              Your Privacy & Health Data Security
            </h2>
            <p className="text-blue-700 text-sm leading-relaxed">
              Your wellness journey and health information are precious. We use end-to-end encryption 
              to protect your sensitive data, and you have complete control over how your information 
              is shared and used. All health data sharing requires your explicit consent and follows 
              HIPAA guidelines.
            </p>
          </div>
        </div>
      </div>

      {/* Privacy Settings Sections */}
      <PrivacySection
        title="Profile & Community Settings"
        description="Control how your wellness progress and profile information is shared within the community"
        icon="User"
        settings={profileSettings}
        category="profile"
      />

      <PrivacySection
        title="Health Data & Medical Information"
        description="Manage sharing of your health considerations and medical data with practitioners"
        icon="Heart"
        settings={healthSettings}
        category="health"
      />

      <PrivacySection
        title="Communication & Social Features"
        description="Control how others can contact you and your visibility in the wellness community"
        icon="MessageCircle"
        settings={communicationSettings}
        category="communication"
      />

      <PrivacySection
        title="Data Collection & Analytics"
        description="Manage data collection, cookies, and third-party integrations"
        icon="Database"
        settings={dataSettings}
        category="data"
      />

      {/* Data Management Actions */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
        <div className="px-6 py-4 border-b border-blue-100">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center">
            <Icon name="Download" size={18} className="mr-2 text-blue-600" />
            Data Management & Rights
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            Export your data or manage your account according to privacy regulations
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Data Export */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-medium text-emerald-800 mb-2 flex items-center">
                <Icon name="Download" size={16} className="mr-2" />
                Export Your Data
              </h4>
              <p className="text-sm text-emerald-700 mb-4">
                Download a complete copy of your wellness data, including progress tracking, 
                session history, and journal entries.
              </p>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDataExport('json')}
                  className="flex-1 bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors duration-200"
                >
                  Export JSON
                </button>
                <button
                  onClick={() => handleDataExport('csv')}
                  className="flex-1 border border-emerald-600 text-emerald-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-emerald-50 transition-colors duration-200"
                >
                  Export CSV
                </button>
              </div>
            </div>

            {/* Account Deletion */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2 flex items-center">
                <Icon name="Trash2" size={16} className="mr-2" />
                Delete Account
              </h4>
              <p className="text-sm text-red-700 mb-4">
                Permanently delete your account and all associated data. This action cannot be undone 
                and will remove all wellness progress and booking history.
              </p>
              
              <button
                onClick={handleDeleteAccount}
                className="w-full bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 transition-colors duration-200"
              >
                Request Account Deletion
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Policy & Contact */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-slate-600 flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-medium text-slate-800 mb-2">Questions About Privacy?</h4>
            <p className="text-sm text-slate-600 mb-4">
              For detailed information about how we collect, use, and protect your data, 
              please review our comprehensive privacy policy. If you have specific questions 
              about your data or privacy rights, our privacy team is here to help.
            </p>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-200">
                Read Privacy Policy
              </button>
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-200">
                Contact Privacy Team
              </button>
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-200">
                Data Protection Rights
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyControls;